import { normalizeTag } from '@platewell/core';

/**
 * A search term split into the free text MiniSearch ranks and the `tag:` / `collection:`
 * operators that decide membership.
 *
 * Values are normalised and lowercased here so every comparison downstream is a plain
 * equality check, matching how tags are deduplicated on write.
 */
export interface ParsedSearch {
  readonly text: string;
  readonly includeTags: readonly string[];
  readonly excludeTags: readonly string[];
  readonly includeCollections: readonly string[];
  readonly excludeCollections: readonly string[];
}

// An unterminated quote runs to the end, so the filter applies while the user is still typing.
const OPERATOR = /(^|\s)(-?)(tag|collection):("([^"]*)"?|\S*)/gi;

export function parseSearchTerm(term: string): ParsedSearch {
  const includeTags: string[] = [];
  const excludeTags: string[] = [];
  const includeCollections: string[] = [];
  const excludeCollections: string[] = [];

  const text = term.replace(
    OPERATOR,
    (_match, lead: string, minus: string, keyword: string, raw: string, quoted?: string) => {
      const value = normalizeTag(quoted ?? raw).toLowerCase();
      // A half-typed `tag:` filters nothing rather than emptying the list.
      if (value !== '') {
        const isTag = keyword.toLowerCase() === 'tag';
        const target =
          minus === '-'
            ? (isTag ? excludeTags : excludeCollections)
            : (isTag ? includeTags : includeCollections);
        target.push(value);
      }
      return lead;
    },
  );

  return {
    text: text.trim().replace(/\s+/g, ' '),
    includeTags,
    excludeTags,
    includeCollections,
    excludeCollections,
  };
}

export function hasSearchFilters(parsed: ParsedSearch): boolean {
  return (
    parsed.includeTags.length > 0 ||
    parsed.excludeTags.length > 0 ||
    parsed.includeCollections.length > 0 ||
    parsed.excludeCollections.length > 0
  );
}
